'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAppDispatch, useAppSelector } from '../../lib/hooks';
import { useSignupMutation } from '../../lib/features/auth/authApiSlice';
import { getErrorMessage } from '../../lib/utils/errorUtils';
import { setUser } from '../../lib/features/auth/authSlice';
import { useAuthTranslations } from '../../lib/hooks/useI18n';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import styles from '../auth.module.css';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [clientError, setClientError] = useState('');
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const authT = useAuthTranslations();

  const [signup, { isLoading, error }] = useSignupMutation();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      router.push('/');
    }
  }, [user, router]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setClientError('');

    if (password !== confirmPassword) {
      setClientError(authT.passwordsDoNotMatch);
      return;
    }

    try {
      const result = await signup({ email, username, password }).unwrap();
      if (result.user) {
        dispatch(setUser(result.user));
      }
      router.push('/');
    } catch (err) {
      console.error('Signup failed:', err);
    }
  };

  const handleLoginClick = () => {
    router.push('/login');
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1 className={styles.title}>{authT.joinGamersNotebook}</h1>

        <form onSubmit={handleSignUp}>
          <div className={styles.formGroup}>
            <Input
              id="email"
              type="email"
              label={authT.email}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              placeholder={authT.enterEmail}
              fullWidth
            />
          </div>

          <div className={styles.formGroup}>
            <Input
              id="username"
              type="text"
              label={authT.username}
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              placeholder={authT.enterUsername}
              fullWidth
            />
          </div>

          <div className={styles.formGroup}>
            <Input
              id="password"
              type="password"
              label={authT.password}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              placeholder={authT.enterPassword}
              fullWidth
            />
          </div>

          <div className={styles.formGroupLast}>
            <Input
              id="confirmPassword"
              type="password"
              label={authT.confirmPassword}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              placeholder={authT.confirmPasswordPlaceholder}
              fullWidth
            />
          </div>

          {(error || clientError) && (
            <div className={styles.error}>
              {clientError || getErrorMessage(error, authT.registrationFailed)}
            </div>
          )}

          <Button
            type="submit"
            variant="success"
            disabled={isLoading}
            loading={isLoading}
            fullWidth
          >
            {isLoading ? authT.creatingAccount : authT.signUp}
          </Button>
        </form>

        <div className={styles.footer}>
          <p className={styles.footerText}>{authT.alreadyHaveAccount}</p>
          <Button
            type="button"
            variant="secondary"
            onClick={handleLoginClick}
            className={styles.buttonSecondary}
          >
            {authT.login}
          </Button>
        </div>
      </div>
    </div>
  );
}
